/**
 * OX 퀴즈 화면 ② — 인사말 영상 + 별명 (Figma `3270:46529`, 2026-08-12 취득)
 *
 * 사용자 요청 원문: *"이 영상이 끝나면 다름 링크의 화면에 왼쪽 고양이 대신 인사말 영상이
 * 나와야한다. 오른쪽에 {유저명일이삼사오} 부분에는 별명이 들어간다"*
 *
 * 🔴 **노드에 고양이가 없다**(실측). 프레임 1920×1200 의 자식은 말풍선(x782, 1078×1090) +
 *    텍스트 2개뿐이고 왼쪽 x 0~782 은 빈 배경이다. 그래서 인사말 영상의 크기·위치에
 *    디자인 근거가 없다 — 사용자 확정(R7)으로 **왼쪽 영역을 비율 유지로 꽉 채운다**.
 *    (dev-steps 잔류 리스크 L1)
 *
 * 회귀 가드: OxQuizFlowScreens.test.jsx
 */
import React from 'react';
import useQuestVideoWatchdog from '../../hooks/useQuestVideoWatchdog';
import './OxQuizFlow.css';

/**
 * 말풍선 첫 줄 문구 — `{별명}요원`.
 *
 * 🔴 **사진찍기 인사말 화면(3366:44224)도 같은 문구를 쓴다** — 그래서 이름에서 `Ox` 를
 *    떼었다. 두 화면이 같은 아이를 다르게 부르지 않도록 조립기는 하나만 둔다.
 * 🔴 별명이 없을 때의 폴백은 **'친구'** 다. 첫 미션 인사(`buildFirstMissionGreeting`)와
 *    같은 단어를 써야 한다 — 두 화면이 같은 아이를 다르게 부르면 세계관이 깨진다.
 *    서버는 공백뿐인 별명을 이미 `null` 로 눕혀 보낸다(계약 C1).
 */
export function buildAgentName(memberNickname) {
  const nickname = typeof memberNickname === 'string' ? memberNickname.trim() : '';
  return `${nickname || '친구'}요원`;
}

export default function OxGreetingScreen({
  videoUrl, memberNickname, onFinished, onAbort,
}) {
  const { videoRef, videoPlaying, isVideoPhase, videoHandlers } = useQuestVideoWatchdog({
    active: true,
    videoUrl,
    onFinished,
    onAbort,
    logLabel: 'OX 인사말 영상',
  });

  // URL 이 없으면 훅이 즉시 onFinished 를 부른다(부모가 건너뛰도록 라우팅하지만 이중 방어).
  if (!isVideoPhase) return null;

  return (
    <div className="oxq-screen oxq-screen-beige oxq-greet" data-testid="ox-greeting-screen">
      {/* 🔴 인사말 영상은 `opacity` 로 숨기지 않는다 — 이 화면은 배경이 밝아 재생 전
          검은 상자가 보이는 편이 "빈 왼쪽" 보다 낫고, 말풍선·별명이 이미 화면을 채운다. */}
      <video
        ref={videoRef}
        /* 재생 시작 전 감춤 — `.oxq-verdict-video` 와 같은 사유(2026-08-18). */
        className={`oxq-greet-video${videoPlaying ? ' oxq-greet-video-playing' : ''}`}
        src={videoUrl}
        autoPlay
        playsInline
        preload="auto"
        aria-label="OX 퀴즈 인사말 영상"
        data-testid="ox-greeting-video"
        {...videoHandlers}
      />
      <span className="oxq-greet-bubble" aria-hidden="true" />
      <p className="oxq-greet-name" data-testid="ox-greeting-name">
        {buildAgentName(memberNickname)}
      </p>
      <div className="oxq-greet-lines" data-testid="ox-greeting-lines">
        <p>OX퀴즈를</p>
        <p>낼거야.</p>
        <p>잘 맞춰봐!</p>
      </div>
    </div>
  );
}
